import axios, { AxiosResponse } from 'axios';
import { ThunkDispatch } from 'redux-thunk';
import { SET_CARD_APPLICATION_ID, SET_CLIENT_ID } from './actionTypes';
import { addReferrerUrl, addSourceToUrl, getUrls, isFinalPage } from '~/utils';
import { contentTypeJSON, handleError, handleSuccess, uploadFiles } from './submitActions';
import { nextPage, setLoading } from './widgetContextActions';
import { IOnboardingState, ISignupState, IState } from '~/sharedInterfaces/IState';
import { Market } from '~/sharedInterfaces/SharedEnums';
import { pushToGTM } from '~/actions/gtmActions';
import { normalizeInput } from '~/utils/validators';

interface IPayload extends IOnboardingState, ISignupState {
  countryCode: Market;
  referenceUrl: string | null;
  landingUrl: string;
  isFinal?: boolean;
  filePaths?: string[];
  applicationReasons?: Array<{ reasonText: string | null; reason: string }>;
}

const { SIGNUP_CARD_PATH } = getUrls();

const handleCardRequest = (payload: IPayload) => {
  const { personalNumber, orgNumber, countryCode } = payload;

  const normalizedPayload = {
    ...payload,
    personalNumber: normalizeInput(personalNumber ?? '', countryCode),
    orgNumber: normalizeInput(orgNumber ?? '', countryCode),
  };

  return (dispatch: ThunkDispatch<any, any, any>, getState: () => IState) => {
    const { widgetContext } = getState();
    // TODO: Remove duplicate events when UX have confirmed that they're done with restructuring in Google Anayltics
    // The long term solution is to use the event structure where we have the `product` inside the payload, instead of the event name.
    // But we're not fully ready to remove the old event yet, so that's why we're sending "double" events right now.
    dispatch(
      pushToGTM({
        event: 'widget-card-application-sent',
        payload: normalizedPayload,
      })
    );
    dispatch(
      pushToGTM({
        event: 'widget-application-sent',
        product: widgetContext.settings.widgetType,
        payload: normalizedPayload,
      })
    );

    dispatch(
      pushToGTM({
        event: 'q_user_a_application_sent',
      })
    );

    if (!widgetContext.cardApplicationId) {
      axios
        .post(SIGNUP_CARD_PATH, normalizedPayload, contentTypeJSON)
        .then(({ data }: AxiosResponse) => {
          dispatch({
            type: SET_CARD_APPLICATION_ID,
            payload: data.applicationUuid,
          });
          dispatch({
            type: SET_CLIENT_ID,
            payload: data.clientId,
          });
          dispatch(
            pushToGTM({
              event: 'widget-card-application-succeeded',
              payload: normalizedPayload,
            })
          );
          dispatch(
            pushToGTM({
              event: 'widget-application-succeeded',
              product: widgetContext.settings.widgetType,
              payload: normalizedPayload,
            })
          );
          dispatch(
            pushToGTM({
              event: 'q_user_a_application_successful',
            })
          );
          dispatch(handleSuccess());
        })
        .catch((error) => {
          dispatch(
            pushToGTM({
              event: 'widget-card-application-rejected',
            })
          );
          dispatch(
            pushToGTM({
              event: 'widget-application-rejected',
              product: widgetContext.settings.widgetType,
            })
          );
          dispatch(
            pushToGTM({
              event: 'q_user_a_application_rejected',
            })
          );
          dispatch(handleError(error));
        });
    } else {
      axios
        .patch(
          `${SIGNUP_CARD_PATH}/${widgetContext.cardApplicationId}`,
          normalizedPayload,
          contentTypeJSON
        )
        .then(() => {
          dispatch(
            pushToGTM({
              event: 'widget-card-application-append-succeeded',
              payload: normalizedPayload,
            })
          );
          dispatch(
            pushToGTM({
              event: 'widget-application-append-succeeded',
              product: widgetContext.settings.widgetType,
              payload: normalizedPayload,
            })
          );
          dispatch(handleSuccess());
        })
        .catch((error) => {
          dispatch(
            pushToGTM({
              event: 'widget-card-application-append-rejected',
            })
          );
          dispatch(
            pushToGTM({
              event: 'widget-application-append-rejected',
              product: widgetContext.settings.widgetType,
            })
          );
          dispatch(handleError(error));
        });
    }
  };
};

// eslint-disable-next-line import/prefer-default-export
export const submitCard = () => {
  return (dispatch: ThunkDispatch<any, any, any>, getState: () => IState) => {
    const { signup, onboarding, widgetContext, error } = getState();
    const { cardPurpose, ...signupWithoutCardPurpose } = signup;
    const { emailAddress, orgNumber, phoneNumber, turnover } = signupWithoutCardPurpose;
    const { documentUpload, ...onboardingWithoutDocumentUpload } = onboarding;
    const urlList = documentUpload || [];
    const userIdentifier = signup.orgNumber || widgetContext.sessionId || '';

    // This will be refactored when we have PATCH functionallity for loan application aswell, since the redux will be refactored also
    const requiredFieldsHasValidData =
      !error.emailAddress?.invalid &&
      !error.orgNumber?.invalid &&
      !error.phoneNumber?.invalid &&
      !error.cardPurpose?.missing &&
      !error.cardPurposeAdditionalInfo?.missing &&
      !error.personalNumber?.invalid;

    const hasRequiredFieldsWithValidData = Boolean(
      emailAddress &&
        orgNumber &&
        (turnover || !widgetContext.pages[widgetContext.currentPage].turnover) &&
        phoneNumber &&
        requiredFieldsHasValidData
    );

    const payload: IPayload = {
      ...signupWithoutCardPurpose,
      ...onboardingWithoutDocumentUpload,
      applicationReasons: cardPurpose?.applicationReasons,
      countryCode: widgetContext.settings.market,
      referenceUrl: addReferrerUrl(),
      landingUrl: addSourceToUrl(),
    };

    if (hasRequiredFieldsWithValidData && urlList && urlList.length) {
      dispatch(setLoading(true));
      uploadFiles(urlList, userIdentifier, widgetContext.settings.widgetType).then((fileList) => {
        payload.filePaths = fileList;
        if (isFinalPage(widgetContext)) payload.isFinal = true;
        dispatch(handleCardRequest(payload));
      });
    } else if (hasRequiredFieldsWithValidData) {
      dispatch(setLoading(true));
      if (isFinalPage(widgetContext)) payload.isFinal = true;
      dispatch(handleCardRequest(payload));
    } else {
      dispatch(nextPage());
    }
  };
};
